import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  TitleSection,
  useUniqueSelector,
} from '@mentimeter/question-modules-shared';
import type { PinOnImageT } from '@mentimeter/question-modules-types';
import { contrastColor } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useImageSize } from './utils/use-image-size';
import { PinOnImageVisualization } from './PinOnImageVisualization';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type SlideStatePinOnImage = { reveal?: boolean };

export const PinOnImagePresentation = () => {
  const {
    useConfig,
    useQuestion,
    useResults,
    useSlideState,
    InteractiveCanvasComponents,
  } = usePresentationContext();
  const {
    question,
    question_styled,
    question_description,
    title_meta,
    layout,
    cropped_image_url,
    show_correct,
    correct_area,
  } = useQuestion();
  const { enableAnimations, mode } = useConfig();
  const [slideState] = useSlideState<SlideStatePinOnImage>();
  const { reveal } = slideState || {};
  const showCorrect =
    show_correct && (reveal || ['screenshot', 'public'].includes(mode));
  const {
    theme: {
      visualizationColors: { fillColors, backgroundColor },
      colors: { neutral },
    },
  } = useRagnar();
  const { results = [] } = useResults<PinOnImageT>();
  const color = fillColors[0] ?? backgroundColor;
  const textColor = contrastColor(color);
  const imageSize = useImageSize(cropped_image_url);
  const selector = useUniqueSelector();

  const ConditionalVisualizationWrapper =
    InteractiveCanvasComponents?.CanvasVisualizationArea
      ? InteractiveCanvasComponents?.CanvasVisualizationArea
      : Box;

  return (
    <Box width="100%" height="100%" alignItems="center" justifyContent="center">
      <TitleSection
        title={question}
        styledTitle={question_styled}
        description={question_description}
        label={title_meta}
        fontSizeOffset={layout?.font_size_offset}
        showTextSizeDropdown={true}
      />

      <ConditionalVisualizationWrapper flex="1" width="100%">
        <PinOnImageVisualization
          selector={selector}
          results={results}
          imageSize={imageSize}
          imageUrl={cropped_image_url}
          color={color}
          textColor={textColor}
          placeholderImageColor={neutral}
          showCorrect={showCorrect}
          showCorrectEnabled={show_correct}
          correctArea={correct_area}
          animationMultiplier={enableAnimations ? 1 : 0}
        />
      </ConditionalVisualizationWrapper>
    </Box>
  );
};
